import { Observable } from 'rxjs';
import { Strings } from '../generated/strings';
import { BatchConnection } from './batch-connection';
import { LifetimeData } from './lifetime-data';
import { NodeConnection } from './node-connection';
import { PowerShell, PowerShellCommand, PowerShellOptions, PowerShellSession, PowerShellSessionRequestOptions } from './powershell';
import { PowerShellBatch, PowerShellBatchResponseItem, PowerShellBatchSession } from './powershell-batch';

/**
 * The PowerShell Connection class.
 */
export class PowerShellConnection {
    /**
     * Initializes a new instance of the PowerShellConnection class.
     *
     * @param lifetimeService the lifetimeService class instance injected.
     * @param nodeConnection the nodeConnection class instance injected.
     * @param batchConnection the batchConnection class instance injected.
     */
    constructor(
        private lifetimeData: LifetimeData,
        private nodeConnection: NodeConnection,
        private batchConnection: BatchConnection) {
    }

    /**
     * @deprecated Deprecated in favor of {@link createAutomaticSession}
     * Create PowerShell Session. By default will recycle a session if one has been created recently.
     *
     * @param nodeName the name of the node to connect to.
     */
    public createSession(nodeName: string): PowerShellSession;

    /**
     * Create PowerShell Session object with persisted shared session by the key.
     *
     * @param nodeName the name of the node to connect to.
     * @param key the unique key to share the session.
     * @param requestOptions the options to apply to every request in this session
     */
    public createSession(nodeName: string, key: string): PowerShellSession;
    public createSession(nodeName: string, key: string, requestOptions: PowerShellSessionRequestOptions): PowerShellSession;
    public createSession(nodeName: string, key?: string, requestOptions?: PowerShellSessionRequestOptions): PowerShellSession {
        if (MsftSme.isNullOrWhiteSpace(key)) {
            // [2/7/2019] Switch to automatic recycled session for instant type of session at default.
            if (requestOptions == null) {
                // if requestOptions is empty.
                return this.createAutomaticSession(nodeName, requestOptions);
            } else if (requestOptions.automatic !== false
                && requestOptions.authenticationMechanism !== 'Credssp'
                && requestOptions.powerShellEndpoint !== PowerShell.smePowerShellEndpoint ) {
                // if implicitly not defined automatic = false AND not CredSSP mode AND not JEA mode.
                return this.createAutomaticSession(nodeName, requestOptions);
            }

            return new PowerShellSession(PowerShell.create(nodeName, this.nodeConnection, null, null, requestOptions));
        }

        const lifeTime = this.lifetimeData.manager.createChildLifetime();
        return new PowerShellSession(PowerShell.create(nodeName, this.nodeConnection, key, lifeTime, requestOptions), lifeTime);
    }

    /**
     * Create PowerShell Session object with auto managed pool of PowerShell runspace sessions.
     * - PowerShellSession object isn't required to be disposed. You can call dispose(), but the function is noop call.
     *
     * @param nodeName the name of the node to connect to.
     * @param requestOptions the options to apply to every request in this session.
     */
    public createAutomaticSession(nodeName: string, requestOptions?: PowerShellSessionRequestOptions): PowerShellSession {
        requestOptions = requestOptions || {};
        requestOptions = { ...requestOptions, ...{ automatic: true } };
        return new PowerShellSession(PowerShell.create(nodeName, this.nodeConnection, null, null, requestOptions));
    }

    /**
     * Runs the powershell command on the session.
     *
     * @param session the PowerShell session object.
     * @param scriptOrCommand the PowerShell command.
     */
    public run(session: PowerShellSession, scriptOrCommand: string | PowerShellCommand): Observable<any>;

    /**
     * Runs the powershell command on the session.
     *
     * @param session the PowerShell session object.
     * @param command the PowerShell command.
     * @param options the PowerShell command options.
     */
    public run(session: PowerShellSession, scriptOrCommand: string | PowerShellCommand, options: PowerShellOptions): Observable<any>;

    /**
     * Runs the powershell command on automatic session.
     *
     * @param nodeName the node name.
     * @param command the PowerShell command.
     * @param options the PowerShell command options.
     */
    public run(nodeName: string, scriptOrCommand: string | PowerShellCommand, options: PowerShellOptions): Observable<any>;

    /**
     * Runs the powershell command on the session.
     *
     * @param sessionOrNodeName the PowerShell session object or node name for automatic session.
     * @param command the PowerShell command.
     * @param options the PowerShell command options.
     */
    public run(
            sessionOrNodeName: PowerShellSession | string,
            scriptOrCommand: string | PowerShellCommand,
            options?: PowerShellOptions): Observable<any> {
        const command = PowerShell.getPowerShellCommand(scriptOrCommand);
        if (typeof scriptOrCommand !== 'string' && !command.command) {
            const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.PowerShellRunCommandFormat.message;
            throw new Error(message);
        }

        let session: PowerShellSession;
        if (typeof sessionOrNodeName === 'string') {
            const nodeName = <string>sessionOrNodeName;
            session = this.createAutomaticSession(nodeName, options);
        } else {
            session = <PowerShellSession>sessionOrNodeName;
        }

        return session.powerShell.run(command, options);
    }

    /**
     * Find existing PowerShellSession by the node name and key.
     *
     * @param nodeName the node name.
     * @param key the key string.
     * @return PowerShellSession the powershell session which is not disposable.
     */
    public find(nodeName: string, key: string): PowerShellSession {
        const ps = PowerShell.find(nodeName, key);
        if (ps) {
            return new PowerShellSession(ps);
        }

        return null;
    }

    /**
     * Cancel the script run.
     * @param session the PowerShell session object.
     */
    public cancel(session: PowerShellSession): Observable<any> {
        return session.powerShell.cancel();
    }

    /**********************************
     * PowerShell Batch Section
     **********************************/

    /**
     * Create PowerShell Batch Session object without persisted session.
     *
     * @param nodeNamesList the Nodes to run the batch commands against.
     */
    public createBatchSession(nodeNamesList: string[]): PowerShellBatchSession;

    /**
     * Create PowerShell Batch Session object with persisted shared session by the key.
     *
     * @param nodeNamesList the Nodes to run the batch commands against.
     * @param key the unique key to share the session.
     * @param requestOptions the options to apply to every request in this session
     */
    public createBatchSession(nodeNamesList: string[], key: string): PowerShellBatchSession;
    public createBatchSession(nodeNamesList: string[], key: string, requestOptions: PowerShellSessionRequestOptions): PowerShellBatchSession;
    public createBatchSession(nodeNamesList: string[], key?: string, requestOptions?: PowerShellSessionRequestOptions): PowerShellBatchSession {

        if (!nodeNamesList || nodeNamesList.length === 0) {
            throw new Error('CreateBatchSession needs a valid non-empty nodes list.');
        }

        if (MsftSme.isNullOrWhiteSpace(key)) {
            return new PowerShellBatchSession(PowerShellBatch.create(nodeNamesList, this.batchConnection, null, null, requestOptions));
        }

        const lifeTime = this.lifetimeData.manager.createChildLifetime();
        return new PowerShellBatchSession(
            PowerShellBatch.create(nodeNamesList, this.batchConnection, key, lifeTime, requestOptions),
            lifeTime);
    }

    /**
     * Runs the powershell commands Batch on the session, with separate command provided for each Node.
     *
     * @param session the PowerShellBatch session object.
     * @param scriptOrCommandList the PowerShell script or command array, corresponding to nodeNamesList.
     */
    public runBatch(session: PowerShellBatchSession, scriptOrCommandList: string[] | PowerShellCommand[]): Observable<PowerShellBatchResponseItem[]>;

    /**
     * Runs the powershell commands on the session, with separate command for each Node.
     *
     * @param session the PowerShellBatch session object.
     * @param scriptOrCommandList the PowerShell script or command array, corresponding to nodeNamesList.
     * @param options the PowerShell command options.
     */
    public runBatch(session: PowerShellBatchSession, scriptOrCommandList: string[] | PowerShellCommand[], options: PowerShellOptions): Observable<PowerShellBatchResponseItem[]>;
    public runBatch(session: PowerShellBatchSession, scriptOrCommandList: string[] | PowerShellCommand[], options?: PowerShellOptions): Observable<PowerShellBatchResponseItem[]> {
        const commandList: PowerShellCommand[] = [];
        for (const scriptOrCommand of scriptOrCommandList) {
            commandList.push(PowerShell.getPowerShellCommand(scriptOrCommand));
        }

        return session.powerShellBatch.run(commandList, options);
    }

    /**
     * Runs the same powershell command on a Batch of Nodes.
     *
     * @param session the PowerShellBatch session object.
     * @param scriptOrCommand the PowerShell command to run on all Nodes.
     */
    public runBatchSingleCommand(session: PowerShellBatchSession, scriptOrCommand: string | PowerShellCommand): Observable<PowerShellBatchResponseItem[]>;

    /**
     * Runs the powershell command on the session.
     *
     * @param session the PowerShellBatch session object.
     * @param scriptOrCommand the PowerShell command to run on all Nodes.
     * @param options the PowerShell command options.
     */
    public runBatchSingleCommand(
        session: PowerShellBatchSession, scriptOrCommand: string | PowerShellCommand, options: PowerShellOptions): Observable<PowerShellBatchResponseItem[]>;
    public runBatchSingleCommand(
        session: PowerShellBatchSession, scriptOrCommand: string | PowerShellCommand, options?: PowerShellOptions): Observable<PowerShellBatchResponseItem[]> {
        return session.powerShellBatch.runSingleCommand(PowerShell.getPowerShellCommand(scriptOrCommand), options);
    }

    /**
     * Find existing PowerShellBatchSession by the node names list and key.
     *
     * @param nodeNames the node names list.
     * @param key the key string.
     * @return PowerShellBatchSession the powershell batch session which is not disposable.
     */
    public findBatchSession(nodeNamesList: string[], key: string): PowerShellBatchSession {
        const ps = PowerShellBatch.find(nodeNamesList, key);
        if (ps) {
            return new PowerShellBatchSession(ps);
        }

        return null;
    }
}
